import NiceModal from "@ebay/nice-modal-react";
import { Skeleton, Stack, Typography } from "@mui/material";
import {
    resetQuestionnaire,
    setEditInvestmentStrategyResult,
    setQuestionnaireAnswers,
    setQuestionnaireRejected,
    setQuestionnaireVersion,
    setStepByName,
    StepperIndexes,
    StepperType,
    stepperContractIdSelector,
    stepperTypeSelector,
    useAppDispatch,
    useAppSelector,
} from "@repo/logic";
import { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useScrollFirstError, useTypedTranslation } from "../../../hooks";
import { DefaultStepProps } from "../../Stepper/CustomStepper";
import { BodyTextM } from "../../Typography";
import { QuestionnaireQueryType } from "../Questionnaire.types";
import { QuestionnaireFormValues, transformDataToApiRequest, useRejectModalTexts } from "./QuestionnairePage.helpers";
import * as Styled from "./QuestionnairePage.styled";
import { QuestionnaireQuestionComponent } from "./QuestionnaireQuestionComponent";
import { useQuestionnaireFormValidator } from "./useQuestionnaireFormValidator";

type QuestionnairePageProps = QuestionnaireQueryType & DefaultStepProps;

export const QuestionnairePage: FC<QuestionnairePageProps> = ({
    setContinueCondition: stepperContinueCondition,
    showConfirmModal,
    hideConfirmModal,
    queries: { questionsQuery, resolveInvestorProfileMutation },
}) => {
    const dispatch = useAppDispatch();
    const stepperType = useAppSelector(stepperTypeSelector);
    const contractId = useAppSelector(stepperContractIdSelector);

    const { data: questionsData, refetch: refetchQuestionsData, isLoading: isLoadingQuestions } = questionsQuery ?? {};
    useEffect(() => {
        if (!questionsData) refetchQuestionsData?.();
    }, [refetchQuestionsData, questionsData]);

    const sustainabilityId = useAppSelector((state) => state.stepper.sustainabilityId);
    const storedAnswers = useAppSelector((state) => state.stepper.questionnaireAnswers);
    const { t } = useTypedTranslation();
    const { mutateAsync: resolveInvestorProfileAsync } = resolveInvestorProfileMutation;
    const validator = useQuestionnaireFormValidator(questionsData?.questions);

    const form = useForm<QuestionnaireFormValues>({
        defaultValues: (storedAnswers ? structuredClone(storedAnswers) : {}) as QuestionnaireFormValues,
        mode: "onChange",
        resolver: validator,
        shouldUnregister: false,
        shouldFocusError: false,
    });

    const { watch, getValues, reset: resetForm, handleSubmit, formState } = form;
    const { dirtyFields } = formState;

    // Track hydration so we only inject stored answers once after mount / data load
    const hydratedRef = useRef(false);

    const questions = useMemo(() => {
        return questionsData?.questions?.slice()?.sort((a, b) => a.order - b.order);
    }, [questionsData]);

    // Hydrate form once when we have stored answers AND questions (so resolver has schema)
    useEffect(() => {
        // Skip hydration if form already dirty (user has started answering)
        if (Object.keys(dirtyFields).length > 0) {
            hydratedRef.current = true;
            return;
        }
        // Only hydrate once
        if (hydratedRef.current || !storedAnswers) return;
        // Deep clone to avoid frozen (Immer) objects/arrays from Redux causing RHF mutation errors
        resetForm(structuredClone(storedAnswers), { keepDefaultValues: false });
        hydratedRef.current = true;
    }, [storedAnswers, resetForm, dirtyFields]);

    // Persist draft answers to redux on any change (could be debounced if needed)
    useEffect(() => {
        const subscription = watch(() => {
            const allValues = getValues();
            // IMPORTANT: Clone before dispatch so Immer freezing in Redux does NOT freeze RHF's live internal state object.
            // If we dispatch RHF's own reference, subsequent user interactions will try to mutate a frozen object and fail.
            dispatch(setQuestionnaireAnswers(structuredClone(allValues)));
        });
        return () => subscription.unsubscribe();
    }, [watch, getValues, dispatch]);

    const containerRef = useRef<HTMLDivElement | null>(null);
    useScrollFirstError<QuestionnaireFormValues>(
        { formState },
        {
            behavior: "smooth",
            root: containerRef.current ?? undefined,
            topOffset: 100,
        },
        { auto: true },
    );

    const validateAndSubmit = useCallback(async () => {
        if (isLoadingQuestions) return false;
        let canContinue = false;

        await handleSubmit(
            // onValid
            async (data) => {
                const dataToSend = transformDataToApiRequest(
                    data,
                    questionsData?.questions,
                    sustainabilityId || "",
                    contractId,
                );
                const result = await resolveInvestorProfileAsync(dataToSend);
                if (result) {
                    dispatch(setEditInvestmentStrategyResult(result));
                    dispatch(setQuestionnaireRejected(false));
                    dispatch(setQuestionnaireVersion(questionsData?.version));
                    canContinue = true;
                }
            },
            // onInvalid
            () => {
                canContinue = false;
            },
        )();

        return canContinue;
    }, [
        isLoadingQuestions,
        handleSubmit,
        questionsData,
        sustainabilityId,
        contractId,
        resolveInvestorProfileAsync,
        dispatch,
    ]);

    useEffect(() => {
        stepperContinueCondition(validateAndSubmit);
    }, [stepperContinueCondition, validateAndSubmit]);

    const rejectModalTexts = useRejectModalTexts(stepperType);
    const handleRejectClick = useCallback(async () => {
        showConfirmModal({
            title: rejectModalTexts.title,
            text: rejectModalTexts.text,
            cancelButtonTitle: rejectModalTexts.cancelButtonTitle,
            confirmButtonTitle: rejectModalTexts.confirmButtonTitle,
            cancelAction: () => {
                hideConfirmModal();
            },
            confirmAction: async () => {
                if (stepperType === StepperType.ChangePensionContribution) {
                    // Change pension contribution reject flow
                    // Call API with rejected: true so the backend records questionnaireSubmitDate.
                    // Without this the validity window is never updated and the questionnaire keeps reappearing on every skip.
                    await resolveInvestorProfileAsync({
                        rejected: true,
                        isNewContract: false,
                        userAnswers: [],
                        sustainabilityId: sustainabilityId || "",
                        contractId,
                    });
                    hideConfirmModal();
                    stepperContinueCondition(() => true); // Reset continue condition on rejected
                    dispatch(setQuestionnaireRejected(true));
                    dispatch(setStepByName(StepperIndexes.changeContributionRecapPage));
                } else if (stepperType === StepperType.PensionQuestionnaire) {
                    // Zavřít modální okno a resetovat stepper
                    NiceModal.hide("Nicemodal-PensionQuestionnaireStepperModal");
                    dispatch(resetQuestionnaire());
                } else {
                    //Pension questionnaire reject flow
                    const result = await resolveInvestorProfileAsync({
                        rejected: true,
                        isNewContract: false,
                        userAnswers: [],
                        sustainabilityId: sustainabilityId || "",
                        contractId,
                    });
                    if (result) {
                        dispatch(setQuestionnaireRejected(true));
                        dispatch(setEditInvestmentStrategyResult(result));
                        dispatch(setQuestionnaireVersion(questionsData?.version));
                    }
                    hideConfirmModal();
                    stepperContinueCondition(() => true); // Reset continue condition on rejected
                    dispatch(setStepByName(StepperIndexes.investmentProfile));
                }
            },
        });
    }, [
        showConfirmModal,
        hideConfirmModal,
        stepperType,
        resolveInvestorProfileAsync,
        sustainabilityId,
        contractId,
        stepperContinueCondition,
        dispatch,
        questionsData?.version,
        rejectModalTexts,
    ]);

    return (
        <Stack ref={containerRef}>
            <Styled.QuestionnaireRejectMessageBodyTextM component="p">
                <Typography component="span">
                    {t("investmentStrategy.pensionQuestionnaire.questionnairePage.rejectMessagePart1")}
                </Typography>
                <Styled.QuestionnaireRejectMessageLink onClick={handleRejectClick}>
                    {t("investmentStrategy.pensionQuestionnaire.questionnairePage.rejectMessagePart2Link")}
                </Styled.QuestionnaireRejectMessageLink>
                <Typography component="span">
                    {t("investmentStrategy.pensionQuestionnaire.questionnairePage.rejectMessagePart3")}
                </Typography>
            </Styled.QuestionnaireRejectMessageBodyTextM>
            <FormProvider {...form}>
                <form>
                    <Stack spacing={2}>
                        {questions?.map((question, index) => (
                            <QuestionnaireQuestionComponent
                                key={question.id}
                                question={question}
                                questionNumber={index + 1}
                            />
                        )) ??
                            Array.from({ length: 6 }, (_, index) => (
                                <Stack key={index}>
                                    <BodyTextM strong>
                                        <Skeleton width="100%" />
                                    </BodyTextM>
                                    <Styled.QuestionnaireQuestionAnswerPaper>
                                        <Skeleton width="100%" />
                                        <Skeleton width="100%" />
                                        <Skeleton width="100%" />
                                    </Styled.QuestionnaireQuestionAnswerPaper>
                                </Stack>
                            ))}
                    </Stack>
                </form>
            </FormProvider>
        </Stack>
    );
};
